import { SetFailureStateAndRouteToFailure } from './actions/FailureActions';
import { TranscriptionRoutePathNames } from './routes';
import { NetworkError, NetworkErrors } from '../utils/Errors';
import { LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from './LoadTranscriptionStrings';
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { TranscriptionTelemetryErrorCodes } from '../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../backend/interfaces/ITranscriptionErrorEvent';
import { FailureContinuation } from './common/enums/failureEnums';

// Keeping this time as 20 seconds as to avoid failure page
// This time will give other components (ex, file upload) to recover from network fluctuation.
const WAIT_TIME_FOR_NETWORK_RECONNECT_MILLIS = 20000; // 20 seconds
const PING_FILE_PATH = "assets/ping_image.png";

export enum NetworkConnectionMonitorConfig {
  ShowTransientError,   // Shows a temporary error page, that usually silently reloads after the network connection is restored
  ShowTerminalError     // Shows a terminal error page, that usually means that the user has to close and reopen the pane
}

export interface NetworkConnectionListener {
  internetDisconnected(): void;
  internetConnected(): void;
}

// Monitors user's network connection status and manages UI accordingly.
export class NetworkConnectionMonitor {
    private history: any;
    private wentOffline: boolean = false;
    private config: NetworkConnectionMonitorConfig | null = null;
    connectedToInternet: boolean = navigator.onLine;

    private listeners: Set<NetworkConnectionListener> = new Set<NetworkConnectionListener>();

    private onOnline = () => {
      this.connectedToInternet = true;
      this.updateListenersForInternetConnected();
      if (!this.wentOffline)
        return;


      // Refresh add-in to start recovery experience.
      location.reload();
    }

    private updateListenersForInternetDisconnected() {
      for (let listener of this.listeners) {
        listener.internetDisconnected()
      }
    }

    private updateListenersForInternetConnected() {
      for (let listener of this.listeners) {
        listener.internetConnected()
      }
    }

    private onOffline = () => {
      this.connectedToInternet = false;
      this.updateListenersForInternetDisconnected();
      // Only show offline error message in Recording and Loading pages.
      if (this.history.location.pathname != TranscriptionRoutePathNames.Recording && this.history.location.pathname != TranscriptionRoutePathNames.Loading)
        return;

      // If current path is recording then execute post processing right away
      if (this.history.location.pathname == TranscriptionRoutePathNames.Recording) {
        this.postOfflineProcessing();
        return;
      }

      // Wait and then ping check
      var networkConnectionObject = this;
      this.waitAndPing().then(function (pingConnected) {
        // If network is reconnected/OK, then no need to post process offline
        // if network is still NOT reconnected, then post process offline.
        if (!pingConnected) {
          networkConnectionObject.postOfflineProcessing();
        }
      });
    }

    private onOfflineTerminal = () => {
      this.connectedToInternet = false;
      this.updateListenersForInternetDisconnected();

      // Only show offline error message in Loading page for file upload.
      if (this.history.location.pathname != TranscriptionRoutePathNames.Loading)
        return;

      // Wait and then ping check,
      var networkConnectionObject = this;
      this.waitAndPing().then(function (pingConnected) {
        // If network is reconnected/OK, then no need to post process offline
        // if network is still NOT reconnected, then post process offline.
        if (!pingConnected) {
          networkConnectionObject.postOfflineTerminalProcessing();
        }
      });
    }

    // waitAndPing method
    // 1. wait for some time, see WAIT_TIME_FOR_NETWORK_RECONNECT for duration,
    // 2. If network already reconnected then return true
    // 3. If network NOT connected, then execute ping check to check network connection,
    // 4. return ping result
    private async waitAndPing(): Promise<boolean> {
      var networkConnectionObject = this;
      return new Promise(function (resolve) {
        setTimeout(function () {
          networkConnectionObject.isPingConnected()
            .then(function (pingConnected: boolean) {
              resolve(pingConnected);
            });
        }, WAIT_TIME_FOR_NETWORK_RECONNECT_MILLIS);
      });
    }

    private postOfflineProcessing() {
      if (this.connectedToInternet) {
        return;
      }
      this.wentOffline = true;

      // Once the user goes offline, we don't care about them going offline again.
      window.removeEventListener('offline', this.onOffline);

      let transcriptionErrorEvent: ITranscriptionErrorEvent = { errorEventMessage: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE, errorEventName: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE };

      // TranscriptionStrings can be null if the user goes offline before TranscriptionStrings is loaded during boot.
      if (TranscriptionStrings != null) {
        if (this.history.location.pathname == TranscriptionRoutePathNames.Recording) {
          transcriptionErrorEvent = { errorEventMessage: TranscriptionStrings.RecordingLostConnectionFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionFailureReason };
        } else {
          transcriptionErrorEvent = { errorEventMessage: TranscriptionStrings.RecordingLostConnectionNoBackupFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionNoBackupFailureReason };
        }
      }

      // Navigate to the failure page.
      SetFailureStateAndRouteToFailure(
        LogEventNames.Warning,
        transcriptionErrorEvent,
        FailureContinuation.None /*failureContinuation*/,
        this.history);
    }

    private postOfflineTerminalProcessing() {
      if (this.connectedToInternet) {
        return;
      }
      const transcriptionErrorEvent: ITranscriptionErrorEvent = { errorEventMessage: TranscriptionStrings.LostInternetConnectionReason, errorEventName: TranscriptionTelemetryErrorCodes.LostInternetConnectionReason };

      // Navigate to the failure page.
      // We can't show a retry button because AL refuses to reconnect after an internet disconnection
      SetFailureStateAndRouteToFailure(
        LogEventNames.Warning,
        transcriptionErrorEvent,
        FailureContinuation.None /*failureContinuation*/,
        this.history);
    }

    // isPingConnected method
    // returns a promise that checks
    // If app is able to ping a file from server
    private isPingConnected(): Promise<boolean> {
      return new Promise<boolean>(function (resolve) {
        var pingHeader = new Headers();
        pingHeader.append('pragma', 'no-cache');
        pingHeader.append('cache-control', 'no-store');
        fetch(PING_FILE_PATH, {
          method: 'GET',
          // Fetch method to NOT return cached value
          cache: "no-store",
          headers: pingHeader
        })
          .then(resp => {
            if (resp.ok) {
              // Network connected and response returned successfuly
              resolve(true);
            } else {
              // Network connected but response returned with error
              resolve(false);
            }
          })
          .catch(function () {
            // Network NOT connected, hence return false
            resolve(false);
          })
      });

    }

    private clearConfig() {
      if (this.config == NetworkConnectionMonitorConfig.ShowTerminalError) {
        window.removeEventListener('offline', this.onOfflineTerminal);
      }

      if (this.config == NetworkConnectionMonitorConfig.ShowTransientError) {
        window.removeEventListener('online', this.onOnline);
        window.removeEventListener('offline', this.onOffline);
      }
      //Marking connectedToInternet to honour navigator once the observer is not observing network via NetworkConnectionMonitor.
      networkConnectionMonitor.connectedToInternet = navigator.onLine;
    }

    public SetConfig(config: NetworkConnectionMonitorConfig, history: any) {
      this.history = history
      if (this.config == config)
        return;

      this.clearConfig();
      switch (config) {
        case NetworkConnectionMonitorConfig.ShowTransientError:
          window.addEventListener('online', this.onOnline);
          window.addEventListener('offline', this.onOffline);
          break;

        case NetworkConnectionMonitorConfig.ShowTerminalError:
          window.addEventListener('offline', this.onOfflineTerminal);
          break;
      }

      this.config = config;
    }

    public addListener(listener: NetworkConnectionListener) {
      this.listeners.add(listener);
    }

    public removeListener(listener: NetworkConnectionListener) {
      this.listeners.delete(listener);
    }

  }

// Throws error if the user is not online
export function CheckNetworkConnection() {
  if (!IsConnectedToNetwork()) {
    throw new NetworkError(NetworkErrors.NoInternetConnection, "User is not connected to the internet");
  }
}

export function IsConnectedToNetwork() {
  return navigator.onLine && GetNetworkConnectionMonitorInstance().connectedToInternet;
}

const networkConnectionMonitor: NetworkConnectionMonitor = new NetworkConnectionMonitor();

export function GetNetworkConnectionMonitorInstance(): NetworkConnectionMonitor {
  return networkConnectionMonitor;
}